import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { playClick } from "@/lib/sound";

const ERA_STYLE = {
  antiquity: {
    labelKey: "eras.antiquity_upper",
    bgImage: "https://images.pexels.com/photos/15126857/pexels-photo-15126857.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tint: "from-amber-900/70 via-orange-900/60 to-stone-950/95",
    accent: "text-amber-300",
    accentBg: "bg-amber-500/15 border-amber-400/40",
    glow: "glow-antiquity",
    piece: "bg-gradient-to-br from-amber-400 to-orange-600 text-stone-950",
  },
  medieval: {
    labelKey: "eras.medieval_upper",
    bgImage: "https://images.pexels.com/photos/38279152/pexels-photo-38279152.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    tint: "from-emerald-950/75 via-stone-800/60 to-stone-950/95",
    accent: "text-emerald-300",
    accentBg: "bg-emerald-500/15 border-emerald-400/40",
    glow: "glow-medieval",
    piece: "bg-gradient-to-br from-emerald-400 to-stone-700 text-stone-950",
  },
  renaissance: {
    labelKey: "eras.renaissance_upper",
    bgImage: "https://images.unsplash.com/photo-1544967082-d9d25d867d66?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    tint: "from-rose-950/70 via-amber-950/60 to-stone-950/95",
    accent: "text-rose-200",
    accentBg: "bg-rose-500/15 border-rose-400/40",
    glow: "glow-renaissance",
    piece: "bg-gradient-to-br from-rose-300 to-amber-700 text-stone-950",
  },
  future: {
    labelKey: "eras.future_upper",
    bgImage: "https://images.unsplash.com/photo-1682933572177-4d853d9d8f17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxjeWJlcnB1bmslMjBmdXR1cmlzdGljJTIwbWFjaGluZSUyMG5lb24lMjB0ZWFsfGVufDB8fHx8MTc4NzY5ODE3Mnww&ixlib=rb-4.1.0&q=85",
    tint: "from-teal-950/75 via-cyan-950/60 to-stone-950/95",
    accent: "text-teal-300",
    accentBg: "bg-teal-500/15 border-teal-400/40",
    glow: "glow-future",
    piece: "bg-gradient-to-br from-teal-400 to-cyan-700 text-stone-950",
  },
};

export default function EraPanel({
  eraKey, eraDef, slotStates, focused, onFocus,
  onDropObject, dragging, setDragging, availableObjects,
  selectedPiece, setSelectedPiece,
}) {
  const { t } = useTranslation();
  const style = ERA_STYLE[eraKey];

  const handleDragStart = (obj) => setDragging({ obj, from: eraKey });
  const handleDragEnd = () => setDragging(null);
  const handleSlotDrop = (slotId) => {
    if (!dragging) return;
    onDropObject(eraKey, slotId, dragging.obj);
    setDragging(null);
  };
  const handleTapPiece = (obj) => {
    if (selectedPiece?.obj?.id === obj.id && selectedPiece?.from === eraKey) {
      setSelectedPiece(null);
    } else {
      playClick();
      setSelectedPiece({ obj, from: eraKey });
    }
  };
  const handleTapSlot = (slotId) => {
    if (!selectedPiece) return;
    // Always route through onDropObject so it can trigger the wrong-piece feedback
    // (playError sound + toast) on touch devices, not only on desktop drag-and-drop.
    onDropObject(eraKey, slotId, selectedPiece.obj);
  };

  return (
    <motion.section
      layout
      transition={{ type: "spring", stiffness: 200, damping: 26 }}
      className={`relative overflow-hidden rounded-3xl border ${focused ? "flex-[2] " + style.glow : "flex-1"} border-white/10 cursor-pointer`}
      onClick={onFocus}
      data-testid={`era-panel-${eraKey}`}
      style={{ minHeight: focused ? 260 : 140 }}
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${style.bgImage})` }}
        aria-hidden
      />
      <div className={`absolute inset-0 bg-gradient-to-b ${style.tint}`} aria-hidden />

      <div className="relative z-10 h-full flex flex-col p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div className={`text-[10px] sm:text-xs font-bold tracking-[0.25em] ${style.accent}`}>
            {t(style.labelKey)}
          </div>
          <div className={`text-xs px-2 py-0.5 rounded-full border ${style.accentBg} font-sans`}>
            {focused ? t("era_panel.focus_badge") : t("era_panel.click_badge")}
          </div>
        </div>

        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-stone-300/70 font-bold">{t("era_panel.elements_kicker")}</div>
            {eraDef.slots.map((slot) => {
              const st = slotStates[slot.id];
              const contentLabel = slot.renders?.[st.state] || st.state;
              const canAcceptDrag = dragging && slot.accepts?.includes(dragging.obj.id);
              const canAcceptTap = selectedPiece && slot.accepts?.includes(selectedPiece.obj.id);
              return (
                <div
                  key={slot.id}
                  onDragOver={(e) => { if (canAcceptDrag) e.preventDefault(); }}
                  onDrop={() => handleSlotDrop(slot.id)}
                  onClick={(e) => { e.stopPropagation(); if (selectedPiece) handleTapSlot(slot.id); }}
                  className={`puzzle-slot rounded-2xl px-3 py-3 border-2 border-dashed ${canAcceptDrag || canAcceptTap ? "dragging-over cursor-pointer" : "border-white/20"} bg-black/35 ${selectedPiece ? "cursor-pointer" : ""}`}
                  data-testid={`slot-${eraKey}-${slot.id}`}
                >
                  <div className="text-[11px] uppercase tracking-widest text-stone-300/70 font-bold">{slot.label}</div>
                  <div className="mt-1 font-serif text-lg text-stone-100">{contentLabel}</div>
                  {st.contents && (
                    <div className="mt-1 text-xs text-amber-300/80 font-sans">{t("era_panel.contains", { id: st.contents })}</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-stone-300/70 font-bold">{t("era_panel.objects_kicker")}</div>
            {availableObjects.length === 0 && (
              <div className="text-sm text-stone-400/70 italic font-sans">{t("era_panel.no_object")}</div>
            )}
            {availableObjects.map((obj) => {
              const isSelected = selectedPiece?.obj?.id === obj.id && selectedPiece?.from === eraKey;
              return (
                <div
                  key={obj.id}
                  draggable
                  onDragStart={() => handleDragStart(obj)}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => { e.stopPropagation(); handleTapPiece(obj); }}
                  className={`puzzle-piece no-select rounded-2xl px-3 py-3 shadow-lg ${style.piece} font-serif font-bold ${isSelected ? "ring-4 ring-amber-300 ring-offset-2 ring-offset-stone-950" : ""}`}
                  data-testid={`piece-${eraKey}-${obj.id}`}
                >
                  <div className="text-xs opacity-80 uppercase tracking-wider font-sans font-medium">
                    {isSelected ? t("era_panel.piece_selected") : t("era_panel.piece_default")}
                  </div>
                  <div className="text-lg">{obj.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
